import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Popover from '@mui/material/Popover'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import { Lock } from 'lucide-react'
import { TimeField } from '../../../shared/form/fields'
import { brand, EASE_ORGANIC } from '../../../styles/theme'
import { useI18n } from '@/core/i18n/useI18n'

export interface HorarioTravado { horario: string | null; andamento: string }

/** Chip de horário. Travado é cinza com cadeado; ajustado à mão leva um ponto. */
export function ChipDeHorario({
  horario, travado, ajustado, destaque, onClick, title,
}: {
  horario: string
  travado?: boolean
  ajustado?: boolean
  destaque?: boolean
  onClick?: (el: HTMLElement) => void
  title?: string
}) {
  const conteudo = (
    <ButtonBase
      disabled={!onClick}
      onClick={(e) => onClick?.(e.currentTarget)}
      sx={{
        position: 'relative',
        minWidth: 72,
        height: 40,
        px: 1.5,
        gap: 0.75,
        borderRadius: '12px',
        fontFamily: 'inherit',
        fontWeight: 700,
        fontSize: '0.95rem',
        fontVariantNumeric: 'tabular-nums',
        border: '1px solid',
        transition: `background-color .15s, border-color .15s, transform .2s ${EASE_ORGANIC}`,
        animation: 'chip-in 260ms ease-out both',
        '@keyframes chip-in': { from: { opacity: 0, transform: 'scale(0.85)' }, to: { opacity: 1, transform: 'scale(1)' } },
        ...(travado
          ? { bgcolor: 'action.hover', borderColor: 'divider', color: 'text.secondary' }
          : destaque
            ? { bgcolor: alpha(brand.folha, 0.16), borderColor: brand.folha, color: 'text.primary' }
            : { bgcolor: alpha(brand.floresta, 0.06), borderColor: alpha(brand.floresta, 0.22), color: brand.floresta }),
        ...(onClick && !travado ? { '&:hover': { bgcolor: alpha(brand.floresta, 0.12), transform: 'translateY(-1px)' } } : {}),
      }}
    >
      {travado && <Lock size={14} />}
      {horario}
      {ajustado && (
        <Box component="span" sx={{ position: 'absolute', top: 5, right: 5, width: 6, height: 6, borderRadius: '50%', bgcolor: brand.folha }} />
      )}
    </ButtonBase>
  )
  return title ? <Tooltip title={title}><span>{conteudo}</span></Tooltip> : conteudo
}

/**
 * Grade de horários. Cada chip é uma carga; tocar abre o ajuste só daquela carga.
 * As travadas, na reprogramação, vêm antes e não abrem nada.
 */
export function HorariosEditor({
  horarios, manuais, travadas = [], onEditar, onPadrao,
}: {
  horarios: string[]
  manuais: ReadonlySet<number>
  travadas?: HorarioTravado[]
  onEditar: (indice: number, horario: string) => void
  onPadrao: (indice: number) => void
}) {
  const { t } = useI18n()
  const [aberto, setAberto] = useState<{ el: HTMLElement; i: number } | null>(null)

  return (
    <Box>
      {travadas.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t('portal.programacao.form.jaComecaram')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {travadas.map((c, i) => (
              <ChipDeHorario key={`t-${i}`} horario={c.horario ?? '--:--'} travado title={t(`portal.programacao.andamento.${c.andamento}`)} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 1, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t('portal.programacao.form.livres')}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))', gap: 1 }} data-testid="grade-horarios">
        {horarios.map((h, i) => (
          <ChipDeHorario key={i} horario={h} ajustado={manuais.has(i)} onClick={(el) => setAberto({ el, i })} />
        ))}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: 'text.secondary' }}>
        {t('portal.programacao.form.toqueParaAjustar')}
      </Typography>

      <Popover
        open={Boolean(aberto)}
        anchorEl={aberto?.el}
        onClose={() => setAberto(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, mt: 1, width: 220, borderRadius: '14px' } } }}
      >
        {aberto && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('portal.programacao.form.ajustarCarga', { n: travadas.length + aberto.i + 1 })}
            </Typography>
            <TimeField label="" value={horarios[aberto.i]} onChange={(v) => v && onEditar(aberto.i, v)} fullWidth />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button size="small" color="inherit" disabled={!manuais.has(aberto.i)} onClick={() => { onPadrao(aberto.i); setAberto(null) }}>
                {t('portal.programacao.form.voltarAoPadrao')}
              </Button>
              <Button size="small" variant="contained" onClick={() => setAberto(null)}>{t('portal.programacao.form.ok')}</Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  )
}
